import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import api from '../services/api';

export default function AdminBaseConocimiento() {
    const CATEGORIAS_PREDEFINIDAS = [
        'Soporte', 'Procedimientos', 'Redes', 'Software', 'Hardware',
        'Sistemas', 'Seguridad', 'Políticas', 'Herramientas', 'Aplicaciones', 'Proveedores'
    ];
    const { user } = useAuth();
    const { alertSuccess, alertError } = useNotificationContext();
    const navigate = useNavigate();

    const [topics, setTopics] = useState([]);
    const [articulos, setArticulos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('topics'); // 'topics' o 'articulos'

    // Estados para topics
    const [showTopicModal, setShowTopicModal] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);
    const [topicForm, setTopicForm] = useState({
        nombre: '',
        descripcion: '',
        icono: '📚',
        color: 'blue',
        orden: 0
    });

    // Estados para artículos
    const [showArticuloModal, setShowArticuloModal] = useState(false);
    const [editingArticulo, setEditingArticulo] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [articuloForm, setArticuloForm] = useState({
        titulo: '',
        descripcion: '',
        contenido: '',
        categoria: '',
        subcategoria: '',
        nivel: 'Básico',
        prioridad: 'Media',
        esPublico: false,
        tags: '',
        topicId: ''
    });

    // Estados para importación JSON
    const [showImportModal, setShowImportModal] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [importing, setImporting] = useState(false);

    // Estados para selección múltiple
    const [selectedArticles, setSelectedArticles] = useState(new Set());
    const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);

    const colores = [
        { value: 'blue', label: 'Azul' },
        { value: 'green', label: 'Verde' },
        { value: 'purple', label: 'Morado' },
        { value: 'orange', label: 'Naranja' },
        { value: 'red', label: 'Rojo' },
        { value: 'yellow', label: 'Amarillo' },
        { value: 'pink', label: 'Rosa' },
        { value: 'indigo', label: 'Índigo' }
    ];

    const iconos = [
        '📚', '🛠️', '🌐', '💻', '🔧', '📊', '⚙️', '🔒', '📱', '💡',
        '🎯', '🚀', '⭐', '🔥', '💎', '🎨', '🔍', '📈', '🛡️', '⚡'
    ];

    const niveles = ['Básico', 'Intermedio', 'Avanzado'];
    const prioridades = ['Baja', 'Media', 'Alta', 'Crítica'];

    useEffect(() => {
        cargarTopics();
        cargarCategorias();
    }, []);

    useEffect(() => {
        if (activeTab === 'articulos') {
            cargarArticulos();
        }
    }, [activeTab]);

    const cargarTopics = async () => {
        try {
            const response = await api.get('/baseconocimiento/topics');
            setTopics(response.data);
        } catch (err) {
            console.error('Error cargando topics:', err);
            alertError('Error al cargar los topics');
        } finally {
            setLoading(false);
        }
    };

    // Estado para acordeón por topic
    const [expandedTopics, setExpandedTopics] = useState(new Set());
    const [articlesByTopic, setArticlesByTopic] = useState({}); // topicId -> artículos

    const toggleTopicExpanded = async (topicId) => {
        const next = new Set(expandedTopics);
        if (next.has(topicId)) {
            next.delete(topicId);
            setExpandedTopics(next);
            return;
        }
        next.add(topicId);
        setExpandedTopics(next);
        // Lazy load de artículos por topic
        if (!articlesByTopic[topicId]) {
            try {
                const params = new URLSearchParams({ topicId, page: 1, pageSize: 1000 });
                const res = await api.get(`/baseconocimiento?${params}`);
                setArticlesByTopic(prev => ({ ...prev, [topicId]: res.data?.articulos || [] }));
            } catch {
                setArticlesByTopic(prev => ({ ...prev, [topicId]: [] }));
            }
        }
    };

    const cargarArticulos = async () => {
        try {
            const response = await api.get('/baseconocimiento');
            setArticulos(response.data.articulos || []);
        } catch (err) {
            console.error('Error cargando artículos:', err);
            alertError('Error al cargar los artículos');
        }
    };

    const cargarCategorias = async () => {
        try {
            const response = await api.get('/baseconocimiento/categorias');
            const backendCats = response.data || [];
            const todas = [...new Set([...CATEGORIAS_PREDEFINIDAS, ...backendCats])].sort();
            setCategorias(todas);
        } catch (err) {
            console.error('Error cargando categorías:', err);
            setCategorias(CATEGORIAS_PREDEFINIDAS);
        }
    };

    const cargarSubcategorias = async (categoria) => {
        try {
            const response = await api.get(`/baseconocimiento/subcategorias?categoria=${categoria}`);
            setSubcategorias(response.data);
        } catch (err) {
            console.error('Error cargando subcategorías:', err);
        }
    };

    // Funciones para Topics
    const abrirModalTopic = (topic = null) => {
        if (topic) {
            setEditingTopic(topic);
            setTopicForm({
                nombre: topic.nombre,
                descripcion: topic.descripcion || '',
                icono: topic.icono,
                color: topic.color,
                orden: topic.orden
            });
        } else {
            setEditingTopic(null);
            setTopicForm({
                nombre: '',
                descripcion: '',
                icono: '📚',
                color: 'blue',
                orden: topics.length
            });
        }
        setShowTopicModal(true);
    };

    const guardarTopic = async () => {
        try {
            if (editingTopic) {
                await api.put(`/topics/${editingTopic.id}`, {
                    ...topicForm,
                    activo: editingTopic.activo
                });
                alertSuccess('Topic actualizado exitosamente');
            } else {
                await api.post('/topics', topicForm);
                alertSuccess('Topic creado exitosamente');
            }
            setShowTopicModal(false);
            cargarTopics();
        } catch (err) {
            console.error('Error guardando topic:', err);
            alertError('Error al guardar el topic');
        }
    };

    const eliminarTopic = async (topic) => {
        const hasArticles = (topic.cantidadArticulos || 0) > 0;
        const message = hasArticles
            ? `¿Está seguro de eliminar el tema "${topic.nombre}"? \n\n¡ATENCIÓN! El tema contiene ${topic.cantidadArticulos} artículos que también serán eliminados. Esta acción no se puede deshacer.`
            : `¿Está seguro de eliminar el tema "${topic.nombre}"? Esta acción no se puede deshacer.`;

        if (!window.confirm(message)) {
            return;
        }

        try {
            await api.delete(`/topics/${topic.id}`);
            alertSuccess('Topic y sus artículos eliminados exitosamente');
            cargarTopics();
            // Limpiar artículos cacheados del topic eliminado
            setArticlesByTopic(prev => {
                const next = { ...prev };
                delete next[topic.id];
                return next;
            });
        } catch (err) {
            console.error('Error eliminando topic:', err);
            alertError('Error al eliminar el topic');
        }
    };

    // Funciones para Artículos
    const abrirModalArticulo = async (articulo = null) => {
        if (articulo) {
            try {
                // Traer datos completos del artículo (incluye contenido y flags)
                const res = await api.get(`/baseconocimiento/${articulo.id}`);
                const detalle = res.data || articulo;
                setEditingArticulo({ ...articulo, ...detalle });
                setArticuloForm({
                    titulo: detalle.titulo,
                    descripcion: detalle.descripcion || '',
                    contenido: detalle.contenido || '',
                    categoria: detalle.categoria || '',
                    subcategoria: detalle.subcategoria || '',
                    nivel: detalle.nivel || 'Básico',
                    prioridad: detalle.prioridad || 'Media',
                    esPublico: !!detalle.esPublico,
                    tags: detalle.tags || '',
                    topicId: (detalle.topic && detalle.topic.id) || detalle.topicId || ''
                });
                if (detalle.categoria) {
                    cargarSubcategorias(detalle.categoria);
                }
                // Cargar adjuntos
                try {
                    const archivosRes = await api.get(`/baseconocimiento/${articulo.id}/archivos`);
                    setEditingArticulo(prev => ({ ...prev, archivos: archivosRes.data }));
                } catch { }
            } catch {
                // fallback a los datos que venían en la grilla
                setEditingArticulo(articulo);
                setArticuloForm(prev => ({ ...prev, titulo: articulo.titulo, descripcion: articulo.descripcion || '' }));
            }
        } else {
            setEditingArticulo(null);
            setArticuloForm({
                titulo: '',
                descripcion: '',
                contenido: '',
                categoria: '',
                subcategoria: '',
                nivel: 'Básico',
                prioridad: 'Media',
                esPublico: false,
                tags: '',
                topicId: selectedTopic ? selectedTopic.id : ''
            });
        }
        setShowArticuloModal(true);
    };

    const guardarArticulo = async () => {
        try {
            if (editingArticulo) {
                await api.put(`/baseconocimiento/${editingArticulo.id}`, articuloForm);
                alertSuccess('Artículo actualizado exitosamente');
            } else {
                await api.post('/baseconocimiento', articuloForm);
                alertSuccess('Artículo creado exitosamente');
            }
            setShowArticuloModal(false);
            cargarArticulos();
        } catch (err) {
            console.error('Error guardando artículo:', err);
            alertError('Error al guardar el artículo');
        }
    };

    const eliminarArticulo = async (articuloId) => {
        if (!window.confirm('¿Está seguro de eliminar este artículo? Esta acción no se puede deshacer.')) {
            return;
        }

        try {
            await api.delete(`/baseconocimiento/${articuloId}`);
            alertSuccess('Artículo eliminado exitosamente');

            // Actualizar la lista de artículos por topic
            const updatedArticlesByTopic = { ...articlesByTopic };
            Object.keys(updatedArticlesByTopic).forEach(topicId => {
                updatedArticlesByTopic[topicId] = updatedArticlesByTopic[topicId].filter(a => a.id !== articuloId);
            });
            setArticlesByTopic(updatedArticlesByTopic);

            // Recargar topics para actualizar contadores
            cargarTopics();
        } catch (err) {
            console.error('Error eliminando artículo:', err);
            alertError('Error al eliminar el artículo');
        }
    };

    const handleCategoriaChange = (categoria) => {
        setArticuloForm(prev => ({ ...prev, categoria, subcategoria: '' }));
        if (categoria) {
            cargarSubcategorias(categoria);
        } else {
            setSubcategorias([]);
        }
    };

    const getNivelColor = (nivel) => {
        switch (nivel) {
            case 'Básico': return 'bg-green-100 text-green-800';
            case 'Intermedio': return 'bg-yellow-100 text-yellow-800';
            case 'Avanzado': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getPrioridadColor = (prioridad) => {
        switch (prioridad) {
            case 'Baja': return 'bg-green-100 text-green-800';
            case 'Media': return 'bg-yellow-100 text-yellow-800';
            case 'Alta': return 'bg-orange-100 text-orange-800';
            case 'Crítica': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Funciones para importación JSON
    const importarArticulos = async () => {
        if (!importJson.trim()) {
            alertError('Por favor, pega el JSON de los artículos');
            return;
        }

        setImporting(true);
        try {
            const articulos = JSON.parse(importJson);
            const articulosArray = Array.isArray(articulos) ? articulos : articulos.articulos || [];

            let importados = 0;
            let errores = 0;

            for (const articulo of articulosArray) {
                try {
                    // Compatibilidad con JSON antiguo: topicid / esPublico como string, etc.
                    const rawTopicId =
                        articulo?.topicId ??
                        articulo?.topicid ??
                        articulo?.topicID ??
                        articulo?.topic_id ??
                        articulo?.topic;

                    const resolvedTopicId = Number(rawTopicId ?? topics?.[0]?.id);

                    const rawEsPublico =
                        articulo?.esPublico ??
                        articulo?.espublico ??
                        articulo?.publico ??
                        false;

                    const esPublico =
                        typeof rawEsPublico === 'boolean'
                            ? rawEsPublico
                            : String(rawEsPublico).trim().toLowerCase() === 'true';

                    const payload = {
                        titulo: String(articulo?.titulo ?? '').trim(),
                        descripcion: articulo?.descripcion ?? null,
                        contenido: String(articulo?.contenido ?? '').trim(),
                        categoria: String(articulo?.categoria ?? '').trim(),
                        subcategoria: articulo?.subcategoria ?? null,
                        nivel: articulo?.nivel ?? undefined,
                        prioridad: articulo?.prioridad ?? undefined,
                        esPublico,
                        tags: articulo?.tags ?? '',
                        topicId: resolvedTopicId,
                    };

                    // Validación mínima para evitar 500 por payload inválido
                    if (!payload.titulo || !payload.contenido || !payload.categoria || Number.isNaN(payload.topicId)) {
                        throw new Error('Payload inválido (faltan campos obligatorios: titulo/contenido/categoria/topicId)');
                    }

                    await api.post('/baseconocimiento', payload);
                    importados++;
                } catch (err) {
                    console.error('Error importando artículo:', articulo.titulo, err);
                    errores++;
                }
            }

            if (importados > 0) {
                alertSuccess(`Importación completada: ${importados} artículos importados${errores > 0 ? `, ${errores} errores` : ''}`);
                setShowImportModal(false);
                setImportJson('');
                cargarTopics(); // Recargar para actualizar contadores
            } else {
                alertError('No se pudo importar ningún artículo');
            }
        } catch (err) {
            console.error('Error parseando JSON:', err);
            alertError('Error en el formato JSON. Verifica que sea válido.');
        } finally {
            setImporting(false);
        }
    };

    // Funciones para selección múltiple
    const toggleArticleSelection = (articleId) => {
        const newSelected = new Set(selectedArticles);
        if (newSelected.has(articleId)) {
            newSelected.delete(articleId);
        } else {
            newSelected.add(articleId);
        }
        setSelectedArticles(newSelected);
    };

    const selectAllArticles = () => {
        const allArticleIds = new Set();
        Object.values(articlesByTopic).flat().forEach(article => {
            allArticleIds.add(article.id);
        });
        setSelectedArticles(allArticleIds);
    };

    const clearSelection = () => {
        setSelectedArticles(new Set());
    };

    const bulkDeleteArticles = async () => {
        if (selectedArticles.size === 0) {
            alertError('No hay artículos seleccionados');
            return;
        }

        if (!window.confirm(`¿Está seguro de eliminar ${selectedArticles.size} artículos? Esta acción no se puede deshacer.`)) {
            return;
        }

        try {
            let deleted = 0;
            let errors = 0;

            for (const articleId of selectedArticles) {
                try {
                    await api.delete(`/baseconocimiento/${articleId}`);
                    deleted++;
                } catch (err) {
                    console.error('Error eliminando artículo:', articleId, err);
                    errors++;
                }
            }

            if (deleted > 0) {
                alertSuccess(`${deleted} artículos eliminados${errors > 0 ? `, ${errors} errores` : ''}`);

                // Actualizar la lista de artículos por topic removiendo los eliminados
                const updatedArticlesByTopic = { ...articlesByTopic };
                Object.keys(updatedArticlesByTopic).forEach(topicId => {
                    updatedArticlesByTopic[topicId] = updatedArticlesByTopic[topicId].filter(a => !selectedArticles.has(a.id));
                });
                setArticlesByTopic(updatedArticlesByTopic);

                clearSelection();
                setBulkDeleteMode(false);
                cargarTopics(); // Recargar para actualizar contadores
            } else {
                alertError('No se pudo eliminar ningún artículo');
            }
        } catch (err) {
            console.error('Error en eliminación masiva:', err);
            alertError('Error al eliminar los artículos');
        }
    };

    if (user?.role !== 'admin' && user?.role !== 'soporte' && user?.role !== 'sap') {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h2 className="text-lg font-semibold text-red-800">Acceso Denegado</h2>
                    <p className="text-red-600">Solo los administradores, personal de soporte y SAP pueden acceder a esta página.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header con botones globales */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Administrar Base de Conocimiento</h1>
                    <p className="text-gray-600 dark:text-gray-300 mt-2">
                        Gestiona topics y artículos de la base de conocimiento
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => abrirModalTopic()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium flex items-center gap-2"
                    >
                        <span>➕</span> Nuevo Topic
                    </button>
                    <button
                        onClick={() => navigate('/base-conocimiento/nuevo')}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium flex items-center gap-2"
                    >
                        <span>📝</span> Nuevo Artículo
                    </button>
                </div>
            </div>

            {/* Tabs de navegación */}
            <div className="mb-6">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('topics')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'topics'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        >
                            Temas/Topics ({topics.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('articulos')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'articulos'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        >
                            Todos los Artículos ({articulos.length})
                        </button>
                    </nav>
                </div>
            </div>

            {/* Content */}
            {activeTab === 'topics' ? (
                <div>
                    {/* Header Topics */}
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Explorar por Temas</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowImportModal(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                            >
                                Importar JSON
                            </button>
                            <button
                                onClick={() => setBulkDeleteMode(!bulkDeleteMode)}
                                className={`px-4 py-2 rounded-lg transition-colors text-sm ${bulkDeleteMode
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : 'bg-orange-600 text-white hover:bg-orange-700'
                                    }`}
                            >
                                {bulkDeleteMode ? 'Cancelar Selección' : 'Eliminar Múltiples'}
                            </button>
                        </div>
                    </div>

                    {/* Acordeón moderno por topic */}
                    <div className="space-y-4">
                        {topics.map((topic) => {
                            const isOpen = expandedTopics.has(topic.id);
                            const articles = articlesByTopic[topic.id];
                            return (
                                <div key={topic.id} className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                                    <div
                                        className="flex items-center justify-between p-4 cursor-pointer select-none"
                                        onClick={() => toggleTopicExpanded(topic.id)}
                                        aria-expanded={isOpen}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-3xl">{topic.icono}</span>
                                            <div>
                                                <div className="text-lg font-semibold text-gray-900 dark:text-white">{topic.nombre}</div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">{topic.descripcion}</div>
                                            </div>
                                            <span className={`ml-3 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200`}>
                                                {topic.cantidadArticulos || 0} artículos
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/base-conocimiento/nuevo?topicId=${topic.id}`);
                                                }}
                                                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                Nuevo artículo
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); abrirModalTopic(topic); }}
                                                className="px-3 py-1.5 border rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                            >Editar topic</button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); eliminarTopic(topic); }}
                                                className="px-3 py-1.5 border rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
                                            >Eliminar topic</button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleTopicExpanded(topic.id); }}
                                                className="px-3 py-1.5 border rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                            >{isOpen ? 'Ocultar' : 'Ver artículos'}</button>
                                        </div>
                                    </div>
                                    {isOpen && (
                                        <div className="border-t border-gray-200 dark:border-gray-700">
                                            {!articles ? (
                                                <div className="p-4 text-sm text-gray-500">Cargando artículos...</div>
                                            ) : articles.length === 0 ? (
                                                <div className="p-4 text-sm text-gray-500">Sin artículos en este topic.</div>
                                            ) : (
                                                <div>
                                                    {/* Controles de selección múltiple */}
                                                    {bulkDeleteMode && (
                                                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-4">
                                                                    <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                                                        {selectedArticles.size} artículos seleccionados
                                                                    </span>
                                                                    <button
                                                                        onClick={selectAllArticles}
                                                                        className="text-xs px-2 py-1 bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded hover:bg-yellow-300 dark:hover:bg-yellow-700"
                                                                    >
                                                                        Seleccionar todos
                                                                    </button>
                                                                    <button
                                                                        onClick={clearSelection}
                                                                        className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                                                                    >
                                                                        Limpiar
                                                                    </button>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={bulkDeleteArticles}
                                                                        disabled={selectedArticles.size === 0}
                                                                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        Eliminar seleccionados ({selectedArticles.size})
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            setBulkDeleteMode(false);
                                                                            clearSelection();
                                                                        }}
                                                                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                                                                    >
                                                                        Cancelar
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                                                        {articles.map(a => (
                                                            <li key={a.id} className="p-4 flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                    {bulkDeleteMode && (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedArticles.has(a.id)}
                                                                            onChange={() => toggleArticleSelection(a.id)}
                                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                        />
                                                                    )}
                                                                    <div>
                                                                        <div className="font-medium text-gray-900 dark:text-white">{a.titulo}</div>
                                                                        <div className="text-xs text-gray-500 dark:text-gray-400">{a.descripcion}</div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {!bulkDeleteMode && (
                                                                        <>
                                                                            <button onClick={() => navigate(`/base-conocimiento/editar/${a.id}`)} className="text-blue-600 hover:text-blue-800">Editar</button>
                                                                            <button onClick={() => eliminarArticulo(a.id)} className="text-red-600 hover:text-red-800">Eliminar</button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {topics.length === 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow border-2 border-dashed border-gray-300 dark:border-gray-700 p-12 text-center">
                                <span className="text-6xl mb-4 block">📁</span>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No hay temas configurados</h3>
                                <p className="text-gray-600 dark:text-gray-300 mb-6">Comienza creando un tema para organizar la base de conocimiento.</p>
                                <button
                                    onClick={() => abrirModalTopic()}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg"
                                >
                                    Crear primer tema
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div>
                    {/* Header Artículos */}
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Artículos</h2>
                        <button
                            onClick={() => navigate('/base-conocimiento/nuevo')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Nuevo Artículo
                        </button>
                    </div>

                    {/* Lista Artículos */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Título
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Categoría
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Nivel
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Prioridad
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Visitas
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {articulos.map((articulo) => (
                                        <tr key={articulo.id}>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {articulo.titulo}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                        {articulo.descripcion}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {articulo.categoria}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getNivelColor(articulo.nivel)}`}>
                                                    {articulo.nivel}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPrioridadColor(articulo.prioridad)}`}>
                                                    {articulo.prioridad}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {articulo.visitas}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                                <button
                                                    onClick={() => navigate(`/base-conocimiento/editar/${articulo.id}`)}
                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={() => eliminarArticulo(articulo.id)}
                                                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                                                >
                                                    Eliminar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Topic */}
            {showTopicModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {editingTopic ? 'Editar Topic' : 'Nuevo Topic'}
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Nombre *
                                </label>
                                <input
                                    type="text"
                                    value={topicForm.nombre}
                                    onChange={(e) => setTopicForm(prev => ({ ...prev, nombre: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="Nombre del topic"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    value={topicForm.descripcion}
                                    onChange={(e) => setTopicForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    rows="3"
                                    placeholder="Descripción del topic"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Icono
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {iconos.map(icono => (
                                        <button
                                            key={icono}
                                            onClick={() => setTopicForm(prev => ({ ...prev, icono }))}
                                            className={`p-2 border rounded-md ${topicForm.icono === icono
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                        >
                                            {icono}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Color
                                </label>
                                <select
                                    value={topicForm.color}
                                    onChange={(e) => setTopicForm(prev => ({ ...prev, color: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    {colores.map(color => (
                                        <option key={color.value} value={color.value}>{color.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Orden
                                </label>
                                <input
                                    type="number"
                                    value={topicForm.orden}
                                    onChange={(e) => setTopicForm(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowTopicModal(false)}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={guardarTopic}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {editingTopic ? 'Actualizar' : 'Crear'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Artículo */}
            {showArticuloModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {editingArticulo ? 'Editar Artículo' : 'Nuevo Artículo'}
                        </h3>

                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Importar:</label>
                                <input
                                    type="file"
                                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const mammoth = await import('mammoth/mammoth.browser');
                                        const arrayBuffer = await file.arrayBuffer();
                                        const result = await mammoth.convertToHtml({ arrayBuffer });
                                        setArticuloForm(prev => ({ ...prev, contenido: result.value }));
                                    }}
                                />
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const pdfjsLib = await import('pdfjs-dist');
                                        const data = new Uint8Array(await file.arrayBuffer());
                                        const pdf = await pdfjsLib.getDocument({ data }).promise;
                                        let html = '';
                                        for (let p = 1; p <= pdf.numPages; p++) {
                                            const page = await pdf.getPage(p);
                                            const textContent = await page.getTextContent();
                                            const text = textContent.items.map(i => i.str).join(' ');
                                            html += `<p>${text}</p>`;
                                        }
                                        setArticuloForm(prev => ({ ...prev, contenido: html }));
                                    }}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Topic *
                                </label>
                                <select
                                    value={articuloForm.topicId}
                                    onChange={(e) => setArticuloForm(prev => ({ ...prev, topicId: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Seleccionar topic</option>
                                    {topics.map(topic => (
                                        <option key={topic.id} value={topic.id}>{topic.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Título *
                                </label>
                                <input
                                    type="text"
                                    value={articuloForm.titulo}
                                    onChange={(e) => setArticuloForm(prev => ({ ...prev, titulo: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="Título del artículo"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    value={articuloForm.descripcion}
                                    onChange={(e) => setArticuloForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    rows="2"
                                    placeholder="Descripción breve del artículo"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Contenido *
                                </label>
                                <div className="bg-white dark:bg-gray-700 rounded-md">
                                    <ReactQuill
                                        theme="snow"
                                        value={articuloForm.contenido}
                                        onChange={(html) => setArticuloForm(prev => ({ ...prev, contenido: html }))}
                                        modules={{
                                            toolbar: [
                                                [{ header: [1, 2, 3, false] }],
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ list: 'ordered' }, { list: 'bullet' }],
                                                ['link', 'image'],
                                                ['clean']
                                            ]
                                        }}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Soporta títulos, listas, imágenes, enlaces y texto enriquecido.</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Categoría
                                    </label>
                                    <select
                                        value={articuloForm.categoria}
                                        onChange={(e) => handleCategoriaChange(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="">Seleccionar categoría</option>
                                        {categorias.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Subcategoría
                                    </label>
                                    <select
                                        value={articuloForm.subcategoria}
                                        onChange={(e) => setArticuloForm(prev => ({ ...prev, subcategoria: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                        disabled={!articuloForm.categoria}
                                    >
                                        <option value="">Seleccionar subcategoría</option>
                                        {subcategorias.map(sub => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Adjuntos */}
                            {editingArticulo && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Adjuntos</label>
                                    <div className="flex items-center gap-2 mb-2">
                                        <input
                                            type="file"
                                            multiple
                                            onChange={async (e) => {
                                                const files = Array.from(e.target.files || []);
                                                if (files.length === 0) return;
                                                const form = new FormData();
                                                files.forEach(f => form.append('archivos', f));
                                                await api.post(`/baseconocimiento/${editingArticulo.id}/archivos`, form, {
                                                    headers: { 'Content-Type': 'multipart/form-data' }
                                                });
                                                // recargar lista
                                                const res = await api.get(`/baseconocimiento/${editingArticulo.id}/archivos`);
                                                setEditingArticulo(prev => ({ ...prev, archivos: res.data }));
                                            }}
                                        />
                                    </div>
                                    <ul className="text-sm divide-y divide-gray-200 dark:divide-gray-700">
                                        {(editingArticulo.archivos || []).map(a => (
                                            <li key={a.id} className="py-2 flex justify-between items-center">
                                                <a className="text-blue-600" href={a.url} target="_blank" rel="noreferrer">
                                                    {a.nombre}
                                                </a>
                                                <button
                                                    className="text-red-600"
                                                    onClick={async () => {
                                                        await api.delete(`/baseconocimiento/archivos/${a.id}`);
                                                        const res = await api.get(`/baseconocimiento/${editingArticulo.id}/archivos`);
                                                        setEditingArticulo(prev => ({ ...prev, archivos: res.data }));
                                                    }}
                                                >
                                                    Eliminar
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Nivel
                                    </label>
                                    <select
                                        value={articuloForm.nivel}
                                        onChange={(e) => setArticuloForm(prev => ({ ...prev, nivel: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        {niveles.map(nivel => (
                                            <option key={nivel} value={nivel}>{nivel}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Prioridad
                                    </label>
                                    <select
                                        value={articuloForm.prioridad}
                                        onChange={(e) => setArticuloForm(prev => ({ ...prev, prioridad: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    >
                                        {prioridades.map(prioridad => (
                                            <option key={prioridad} value={prioridad}>{prioridad}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Tags
                                </label>
                                <input
                                    type="text"
                                    value={articuloForm.tags}
                                    onChange={(e) => setArticuloForm(prev => ({ ...prev, tags: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="Tags separados por comas (ej: VPN, Redes, Conectividad)"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="esPublico"
                                    checked={articuloForm.esPublico}
                                    onChange={(e) => setArticuloForm(prev => ({ ...prev, esPublico: e.target.checked }))}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="esPublico" className="ml-2 block text-sm text-gray-900 dark:text-white">
                                    Artículo público (visible para todos los usuarios)
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowArticuloModal(false)}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={guardarArticulo}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {editingArticulo ? 'Actualizar' : 'Crear'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Importar JSON */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Importar Artículos desde JSON
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    JSON de Artículos
                                </label>
                                <textarea
                                    value={importJson}
                                    onChange={(e) => setImportJson(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                    rows="15"
                                    placeholder={`Pega aquí el JSON de los artículos. Ejemplo:
{
  "articulos": [
    {
      "titulo": "Configuración de equipos nuevos",
      "descripcion": "Guía paso a paso para configurar equipos",
      "contenido": "<h1>Configuración...</h1>...",
      "categoria": "Hardware",
      "esPublico": true,
      "tags": "configuracion, equipos, setup",
      "topicId": "1"
    }
  ]
}`}
                                />
                            </div>

                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    Formato esperado:
                                </h4>
                                <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                                    <li>• <strong>titulo:</strong> Título del artículo</li>
                                    <li>• <strong>descripcion:</strong> Descripción breve</li>
                                    <li>• <strong>contenido:</strong> Contenido en HTML</li>
                                    <li>• <strong>categoria:</strong> Hardware, Software, Redes, etc.</li>
                                    <li>• <strong>esPublico:</strong> true/false</li>
                                    <li>• <strong>tags:</strong> Tags separados por comas</li>
                                    <li>• <strong>topicId:</strong> ID del topic (1-5)</li>
                                </ul>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => {
                                    setShowImportModal(false);
                                    setImportJson('');
                                }}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                disabled={importing}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={importarArticulos}
                                disabled={importing || !importJson.trim()}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {importing ? 'Importando...' : 'Importar Artículos'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}





